import { useState, useEffect } from 'react';
import UserCreateModal from './modal-created';
import UserEditModal from './modal-edit';
import UserService from './service';
import { Header } from 'components/Header/Header';
import DeleteModal from './moda-delete';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';

type User = {
  id: number;
  last_name: string;
  email: string;
  mobile_phone: string;
  userType: string;
  active: boolean;
  first_name: string;
  phone_number: string;
  role_id: number;
  otpMethod: string;
  type_send_otp: string;
  is_submerchant: boolean;
  date_of_birth: string;
};

type Invitation = {
  id: number;
  invite_code: string;
  code_used: boolean;
  date_created: string;
  date_expiration: string;
  user_id: number;
  first_name: string;
  last_name: string;
};

type TabType = 'users' | 'invitations';

const UsersComponent = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('users');

  // Users state
  const [users, setUsers] = useState<User[]>([]);
  const [searchText, setSearchText] = useState('');
  const [userActive, setUserActive] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Invitations state
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [invitationStatus, setInvitationStatus] = useState(true);
  const [invitationSearchText, setInvitationSearchText] = useState('');

  // Shared pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  //delete modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedInvitationId, setSelectedInvitationId] = useState<number | null>(null);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else {
      fetchInvitations();
    }
  }, [
    activeTab,
    currentPage,
    pageSize,
    searchText,
    userActive,
    invitationStatus,
    invitationSearchText,
  ]);

  const fetchUsers = async (page: number = currentPage, size: number = rowsPerPage) => {
    try {
      const response = await UserService.getFilteredUsers(
        page,
        size,
        searchText,
        'Staff',
        '',
        '',
        userActive,
        'subdominio1'
      );
      console.log('response', response.data);
      const data = response.data as { users: User[]; total: number };
      setUsers(data.users);
      setTotalItems(data.total); // Asegúrate de usar el total correctamente
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchInvitations = async () => {
    try {
      const response = await UserService.getInvitation(1, {
        page: currentPage,
        limit: pageSize,
        search: invitationSearchText,
        status: invitationStatus,
        sort: 'date_created', // o el campo por el que quieras ordenar
        order: 'desc', // o 'asc'
      });

      const data = response.data as { response: Invitation[]; total: number };
      setInvitations((data as { response: Invitation[] }).response);
      setTotalItems(data.total);
    } catch (error) {
      console.error('Error fetching invitations:', error);
    }
  };

  // User actions
  const handleUserDelete = async (user: User) => {
    try {
      await UserService.deleteUser(user.id);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const handleDeleteInvitation = async (id: number) => {
    try {
      await UserService.deleteInvitationGenerated(id);
      fetchInvitations();
      setModalOpen(false);
    } catch (error) {
      console.error('Error deleting invitation:', error);
    }
  };

  const openModal = (id: number) => {
    setSelectedInvitationId(id);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedInvitationId(null);
    setModalOpen(false);
  };

  const handleUserArchive = async (user: User) => {
    try {
      await UserService.archiveUser(user.id);
      fetchUsers();
    } catch (error) {
      console.error('Error archiving user:', error);
    }
  };

  // Invitation actions
  const handleToggleInvitation = async (invitationId: number, currentStatus: boolean) => {
    try {
      await UserService.updateInvitationUsed(invitationId, !currentStatus);
      fetchInvitations();
    } catch (error) {
      console.error('Error toggling invitation:', error);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setCurrentPage(newPage); // Actualiza la página
    fetchUsers(newPage, rowsPerPage); // Llama a la API con los valores correctos
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage); // Actualiza el número de filas por página
    setCurrentPage(0); // Reinicia a la primera página
    fetchUsers(0, newRowsPerPage); // Llama a la API con los valores actualizados
  };

  return (
    <>
      <Header />
      <Box sx={{ p: 3, bgcolor: 'background.default', height: '100vh' }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 3 }}>
          <Stack
            direction='row'
            spacing={2}
            justifyContent={{ xs: 'center', md: 'flex-start' }}
            flexWrap='wrap'
          >
            <Button
              onClick={() => setActiveTab('users')}
              variant='text'
              sx={{
                py: 1,
                px: 3,
                fontWeight: 'medium',
                color: activeTab === 'users' ? 'primary.light' : 'text.secondary',
                borderBottom: activeTab === 'users' ? '2px solid' : 'none',
                borderColor: activeTab === 'users' ? 'primary.main' : 'transparent',
                '&:hover': { color: 'primary.light' },
              }}
            >
              Users
            </Button>
            <Button
              onClick={() => setActiveTab('invitations')}
              variant='text'
              sx={{
                py: 1,
                px: 3,
                fontWeight: 'medium',
                color: activeTab === 'invitations' ? 'primary.light' : 'text.secondary',
                borderBottom: activeTab === 'invitations' ? '2px solid' : 'none',
                borderColor: activeTab === 'invitations' ? 'primary.main' : 'transparent',
                '&:hover': { color: 'primary.light' },
              }}
            >
              Invitations
            </Button>
          </Stack>

          {/* Users Tab Content */}
          {activeTab === 'users' && (
            <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
              <Stack spacing={4}>
                <Box>
                  <Typography variant='h4' fontWeight='bold' gutterBottom>
                    Users Management
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                </Box>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={() => setShowCreateModal(true)}
                  >
                    Create User
                  </Button>
                  <TextField
                    variant='outlined'
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder='Search users'
                    size='small'
                    sx={{ maxWidth: 300 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={userActive}
                        onChange={() => setUserActive(!userActive)}
                        color='primary'
                      />
                    }
                    label='Active Users'
                  />
                </Box>

                {/* Table */}
                <TableContainer
                  component={Paper}
                  sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'primary.main' }}>
                        <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold', textAlign: 'center' }}>
                          Name
                        </TableCell>
                        <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold', textAlign: 'center' }}>
                          Email
                        </TableCell>
                        <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold', textAlign: 'center' }}>
                          Active
                        </TableCell>
                        <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold', textAlign: 'center' }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align='center'>
                            <Typography color='text.secondary'>No users found</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        users.map((user) => (
                          <TableRow
                            key={user.id}
                            sx={{
                              '&:nth-of-type(odd)': { bgcolor: 'var(--surface-2)' },
                              '&:hover': { bgcolor: 'var(--surface-3)' },
                            }}
                          >
                            <TableCell align='center'>{`${user.first_name} ${user.last_name}`}</TableCell>
                            <TableCell align='center'>{user.email}</TableCell>
                            <TableCell align='center'>{user.active ? 'Yes' : 'No'}</TableCell>
                            <TableCell align='center'>
                              <Stack direction='row' spacing={1} justifyContent='center'>
                                <Button
                                  variant='outlined'
                                  size='small'
                                  sx={{
                                    mr: 1,
                                    borderColor: 'divider',
                                    color: 'text.primary',
                                    '&:hover': {
                                      backgroundColor: 'text.primary',
                                      color: 'background.paper',
                                      borderColor: 'text.primary',
                                    },
                                  }}
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setShowEditModal(true);
                                  }}
                                >
                                  <EditIcon /> {/* Ícono de edición */}
                                </Button>
                                <Button
                                  variant='outlined'
                                  size='small'
                                  sx={{
                                    mr: 1,
                                    borderColor: 'error.main',
                                    color: 'error.main',
                                    '&:hover': {
                                      backgroundColor: 'error.main',
                                      color: 'error.contrastText',
                                      borderColor: 'error.main',
                                    },
                                  }}
                                  onClick={() => handleUserDelete(user)}
                                >
                                  <DeleteIcon /> {/* Ícono de eliminación */}
                                </Button>
                                <Button
                                  variant='outlined'
                                  size='small'
                                  sx={{
                                    borderColor: 'divider',
                                    color: 'text.primary',
                                    '&:hover': {
                                      backgroundColor: 'text.primary',
                                      color: 'background.paper',
                                      borderColor: 'text.primary',
                                    },
                                  }}
                                  onClick={() => handleUserArchive(user)}
                                >
                                  <ArchiveIcon /> {/* Ícono de archivo */}
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component='div'
                    count={totalItems} // Total de elementos
                    rowsPerPage={rowsPerPage} // Número de filas por página
                    page={currentPage} // Página actual
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableContainer>
              </Stack>
            </Box>
          )}

          {/* Invitations Tab Content */}
          {activeTab === 'invitations' && (
            <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
              <Stack spacing={4}>
                {/* Header */}
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Box>
                    <Typography variant='h4' fontWeight='bold' gutterBottom>
                      Invitations Management
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                      variant='outlined'
                      value={invitationSearchText}
                      onChange={(e) => setInvitationSearchText(e.target.value)}
                      placeholder='Search by invitation code'
                      size='small'
                      sx={{ maxWidth: 300 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={invitationStatus}
                          onChange={() => setInvitationStatus(!invitationStatus)}
                          color='primary'
                        />
                      }
                      label='Active Invitations'
                    />
                  </Box>
                </Box>

                {/* Table */}
                <TableContainer
                  component={Paper}
                  sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'primary.main' }}>
                        <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>
                          Guest User
                        </TableCell>
                        <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>
                          Invitation Code
                        </TableCell>
                        <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>
                          Created At
                        </TableCell>
                        <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>
                          Expires At
                        </TableCell>
                        <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold' }} align='right'>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invitations.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align='center'>
                            <Typography color='text.secondary'>No invitations found</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        invitations
                          .filter((invitation) => !invitation.code_used)
                          .map((invitation) => (
                            <TableRow
                              key={invitation.id}
                              sx={{
                                '&:nth-of-type(odd)': { bgcolor: 'var(--surface-2)' },
                                '&:hover': { bgcolor: 'var(--surface-3)' },
                              }}
                            >
                              <TableCell>{`${invitation.first_name} ${invitation.last_name}`}</TableCell>
                              <TableCell>{invitation.invite_code}</TableCell>
                              <TableCell>
                                <Chip
                                  label={invitation.code_used ? 'Unanswered' : 'Pending'}
                                  color={invitation.code_used ? 'success' : 'error'}
                                  size='small'
                                />
                              </TableCell>
                              <TableCell>
                                {new Date(invitation.date_created).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                {new Date(invitation.date_expiration).toLocaleDateString()}
                              </TableCell>
                              <TableCell align='right'>
                                <Button
                                  variant='outlined'
                                  size='small'
                                  sx={{
                                    mr: 1,
                                    borderColor: 'error.main',
                                    color: 'error.main',
                                    '&:hover': {
                                      backgroundColor: 'error.main',
                                      color: 'error.contrastText',
                                      borderColor: 'error.main',
                                    },
                                  }}
                                  onClick={() => openModal(invitation.id)}
                                >
                                  <DeleteIcon /> {/* Ícono de eliminación */}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                      )}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component='div'
                    count={invitations.length}
                    rowsPerPage={currentPage}
                    page={pageSize}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableContainer>
              </Stack>
            </Box>
          )}

          {/* Modals */}
          {showCreateModal && (
            <UserCreateModal onClose={() => setShowCreateModal(false)} onUserCreated={fetchUsers} />
          )}
          {showEditModal && selectedUser && (
            <UserEditModal
              user={selectedUser}
              onClose={() => setShowEditModal(false)}
              onUserUpdated={fetchUsers}
            />
          )}

          <DeleteModal
            open={modalOpen}
            onClose={closeModal}
            onConfirm={() => {
              if (selectedInvitationId !== null) {
                handleDeleteInvitation(selectedInvitationId);
              }
            }}
          />
        </Box>
      </Box>
    </>
  );
};

export default UsersComponent;
